/**
 * A person reviews what OttoTester found in a document once each read
 * finishes: the document's type, and the list of what it found, each
 * beside the passage it rests on.
 *
 * Every document is reviewed, whatever its type. OttoTester reads them
 * all the same way, so a Reference document's list can be just as wrong
 * as a Requirements one. Confirming removes the proposed tests of anything
 * unticked; for Requirements and Defects the kept items also start
 * counting toward coverage. The type is picked here too, and saved with
 * the confirm, so the person settles both in one step.
 *
 * Three pieces, one file because they share the popup:
 * - `DocumentReviewDialog`, the popup itself.
 * - `DocumentPartsReview`, the pill on a document's row that opens it.
 * - `DocumentReviewQueue`, mounted by the Tests page, which opens the
 *   popup by itself for each document waiting for review. It lives on the
 *   page rather than in the documents list because that list is only
 *   mounted while "Manage sources" is open.
 *
 * A list found before quotes were kept, or from text the document no
 * longer holds, cannot be confirmed. The popup offers to read just that
 * document again instead of the app-wide "Suggest again".
 */
import { useEffect, useState, useSyncExternalStore } from 'react'
import { Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui'
import { applicationsAPI } from '@/lib/api'
import type { DocumentKind, ReferenceDoc } from '@/types'
import { DOCUMENT_KIND_HINT, DocumentKindControl, countsAsNote } from './DocumentKindControl'

/** Mount a fresh review whenever the saved list changes, so its ticks
 * start from that list. The text can stay the same while the list does
 * not: a list saved without quotes is found again, with them, from
 * unchanged text. Polls that bring back the same list keep the ticks. */
export function documentPartsReviewKey(doc: ReferenceDoc): string {
  const parts = (doc.parts ?? [])
    .map(p => `${p.id}${p.quote ? '+q' : ''}${p.excluded ? '+x' : ''}${p.missingFromSourceAt ? '+m' : ''}`)
    .join(',')
  return `${doc.id}:${doc.partsContentHash ?? ''}:${parts}`
}

const READING: ReadonlyArray<ReferenceDoc['extractionStatus']> = ['pending', 'extracting', 'retrying']

const NOUN: Record<DocumentKind, [one: string, many: string]> = {
  requirements: ['requirement', 'requirements'],
  defects: ['defect', 'defects'],
  reference: ['topic', 'topics'],
}

/** "1 requirement", "3 topics". */
function countOf(kind: DocumentKind, n: number): string {
  return `${n} ${NOUN[kind][n === 1 ? 0 : 1]}`
}

function partsInDocument(doc: ReferenceDoc) {
  return (doc.parts ?? []).filter(p => !p.missingFromSourceAt)
}

/** The document's text changed after this list was found. */
function isOutdated(doc: ReferenceDoc): boolean {
  return doc.partsContentHash !== doc.contentHash
}

/** The list cannot be confirmed as it stands: something in it has no
 * quote, or it was found from older text. Reading the document again
 * fixes both. */
function needsReadAgain(doc: ReferenceDoc): boolean {
  return isOutdated(doc) || partsInDocument(doc).some(p => !p.quote)
}

function isConfirmed(doc: ReferenceDoc): boolean {
  return Boolean(doc.partsConfirmedAt) && !isOutdated(doc)
}

/** A finished read found something, and nobody has confirmed it yet. */
export function isWaitingForReview(doc: ReferenceDoc): boolean {
  return doc.extractionStatus === 'done' && partsInDocument(doc).length > 0 && !isConfirmed(doc)
}

/** How many review popups the rows have open. The queue waits while any
 * is, so a read finishing behind a popup the person opened from a row
 * never stacks a second one on top. Shared through a module-level count
 * because the rows and the queue sit in different parts of the page. */
let rowReviewsOpen = 0
const rowReviewListeners = new Set<() => void>()
function changeRowReviewsOpen(delta: number) {
  rowReviewsOpen += delta
  rowReviewListeners.forEach(l => l())
}
function useRowReviewOpen(): boolean {
  return useSyncExternalStore(
    listener => {
      rowReviewListeners.add(listener)
      return () => { rowReviewListeners.delete(listener) }
    },
    () => rowReviewsOpen > 0,
  )
}

/** The popup. Keyed by the caller on `documentPartsReviewKey`, so a new
 * list starts from its own ticks. Closing it without confirming is
 * "Later": nothing is saved. */
export function DocumentReviewDialog({
  appId,
  doc,
  onClose,
  onChange,
  waiting,
}: {
  appId: string
  doc: ReferenceDoc
  onClose: () => void
  onChange: () => void | Promise<void>
  /** How many documents are waiting, when opened from the queue. */
  waiting?: number
}) {
  const parts = doc.parts ?? []
  const inDocument = partsInDocument(doc)
  const [kind, setKind] = useState<DocumentKind>(doc.effectiveKind ?? 'reference')
  const [kept, setKept] = useState<ReadonlySet<string>>(
    () => new Set(inDocument.filter(p => p.quote && !p.excluded).map(p => p.id)),
  )
  const [busy, setBusy] = useState<'confirm' | 'read' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const readAgain = needsReadAgain(doc)
  const many = NOUN[kind][1]

  function toggle(id: string) {
    setKept(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function confirm() {
    if (!doc.partsContentHash) return
    setBusy('confirm')
    setError(null)
    try {
      await applicationsAPI.confirmReferenceDocParts(appId, doc.id, [...kept], doc.partsContentHash, kind)
      onClose()
      await onChange()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't confirm this list. Try again.")
    } finally {
      setBusy(null)
    }
  }

  /** Read only this document again. The same call the row's Retry and
   * "Re-propose to update" make; the server refuses while it is already
   * being read. */
  async function readDocumentAgain() {
    setBusy('read')
    setError(null)
    try {
      await applicationsAPI.reproposeForDoc(appId, doc.id)
      onClose()
      await onChange()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't read this document again. Try again.")
    } finally {
      setBusy(null)
    }
  }

  return (
    <Dialog open onOpenChange={open => { if (!open && !busy) onClose() }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader className="pr-12">
          <div className="flex items-start gap-2">
            <DialogTitle className="flex-1 text-base">Review what OttoTester found</DialogTitle>
            {waiting !== undefined && waiting > 1 && (
              <span className="whitespace-nowrap text-xs text-muted-foreground">1 of {waiting} waiting</span>
            )}
          </div>
          <p className="truncate text-xs text-muted-foreground" title={doc.filename}>{doc.filename}</p>
        </DialogHeader>
        <DialogBody className="text-xs">
          <DocumentKindControl
            label="This document is"
            groupName={`What ${doc.filename} is`}
            selected={kind}
            // The note says who chose the saved type; once the person picks
            // another one here, it no longer describes the selection.
            note={kind === (doc.effectiveKind ?? 'reference') ? countsAsNote(doc) : null}
            busy={busy !== null}
            onPick={setKind}
            className="text-xs"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">{DOCUMENT_KIND_HINT[kind]}</p>
          {readAgain ? (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-2 text-amber-700 dark:text-amber-400">
              <span className="min-w-[220px] flex-1">
                {isOutdated(doc)
                  ? 'This document changed after these were found, so they can’t be confirmed. Reading it again finds them in the new text.'
                  : 'These were found before OttoTester kept the document’s words, so they can’t be confirmed. Reading it again finds them with quotes.'}
              </span>
              <Button size="sm" disabled={busy !== null} onClick={() => void readDocumentAgain()}>
                {busy === 'read' ? 'Starting…' : 'Read this document again'}
              </Button>
            </div>
          ) : (
            <p className="mt-3 text-muted-foreground">
              Untick any that aren&apos;t {many}. Unticked ones lose their proposed tests.
            </p>
          )}
          <ul className="mt-2 divide-y divide-border rounded-md border border-border bg-background">
            {parts.map(p => {
              const missing = Boolean(p.missingFromSourceAt)
              const selectable = !missing && Boolean(p.quote)
              const checked = selectable && kept.has(p.id)
              return (
                <li key={p.id} className={`grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 p-2 ${checked ? '' : 'opacity-60'}`}>
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={checked}
                    disabled={!selectable || busy !== null}
                    onChange={() => toggle(p.id)}
                    aria-label={`Keep ${p.title}`}
                  />
                  <span className="font-semibold">
                    {p.title}
                    {missing && <span className="ml-2 font-normal text-muted-foreground">No longer in the document</span>}
                    {!missing && !p.quote && <span className="ml-2 font-normal text-muted-foreground">No quote yet</span>}
                  </span>
                  {p.quote && (
                    <blockquote className="col-start-2 rounded-r border-l-2 border-muted-foreground/40 bg-muted/40 px-2 py-1 text-[11px]">
                      &ldquo;{p.quote}&rdquo;
                    </blockquote>
                  )}
                </li>
              )
            })}
          </ul>
          {error && <p className="mt-2 text-destructive" role="alert">{error}</p>}
        </DialogBody>
        <DialogFooter className="items-center gap-2 text-xs sm:space-x-0">
          <span className="mr-auto text-muted-foreground">
            {readAgain
              ? 'Only this document is read again. Your other documents and their tests stay as they are.'
              : `${kept.size} of ${inDocument.length} kept.`}
          </span>
          <Button size="sm" variant="outline" disabled={busy !== null} onClick={onClose}>Later</Button>
          <Button size="sm" disabled={busy !== null || readAgain || !doc.partsContentHash} onClick={() => void confirm()}>
            {busy === 'confirm' ? 'Confirming…' : `Confirm ${countOf(kind, kept.size)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The pill on a document's row: "15 topics found · Review" until
 * confirmed, then "14 topics". Either opens the popup. */
export function DocumentPartsReview({
  appId,
  doc,
  onChange,
}: {
  appId: string
  doc: ReferenceDoc
  onChange: () => void | Promise<void>
}) {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!open) return
    changeRowReviewsOpen(1)
    return () => changeRowReviewsOpen(-1)
  }, [open])
  const inDocument = partsInDocument(doc)
  if (READING.includes(doc.extractionStatus) || inDocument.length === 0) return null

  const kind = doc.effectiveKind ?? 'reference'
  const confirmed = isConfirmed(doc)
  const countedNow = inDocument.filter(p => !p.excluded).length

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={confirmed
          ? 'text-[11px] font-semibold text-primary underline hover:no-underline'
          : 'inline-flex items-center rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-400'}
      >
        {confirmed ? countOf(kind, countedNow) : `${countOf(kind, inDocument.length)} found · Review`}
      </button>
      {open && (
        <DocumentReviewDialog
          key={documentPartsReviewKey(doc)}
          appId={appId}
          doc={doc}
          onClose={() => setOpen(false)}
          onChange={onChange}
        />
      )}
    </div>
  )
}

/** What a "Later" remembers: this list from this read. A new read opens
 * again even when it finds the same list for the same text, which reuses
 * the saved parts, so the list alone would not tell the two apart. */
function putOffKey(doc: ReferenceDoc): string {
  return `${documentPartsReviewKey(doc)}@${String(doc.extractedAt ?? '')}`
}

/** Opens the popup by itself for each document waiting for review, one
 * at a time. A read that finishes while the page is open joins the
 * queue on the next poll, behind the document already open, so it never
 * swaps out a review in progress. Put off with "Later", a document waits
 * until the next visit: the put-off list lives only as long as this
 * mount. Waits while a popup opened from a row is showing. */
export function DocumentReviewQueue({
  appId,
  docs,
  onChange,
}: {
  appId: string
  docs: ReferenceDoc[]
  onChange: () => void | Promise<void>
}) {
  const [putOff, setPutOff] = useState<ReadonlySet<string>>(() => new Set())
  const [activeId, setActiveId] = useState<string | null>(null)
  const rowReviewOpen = useRowReviewOpen()
  const waiting = docs.filter(d => isWaitingForReview(d) && !putOff.has(putOffKey(d)))
  const doc = waiting.find(d => d.id === activeId) ?? waiting[0]
  const docId = doc?.id ?? null
  useEffect(() => { setActiveId(docId) }, [docId])
  if (!doc || rowReviewOpen) return null
  const key = putOffKey(doc)
  return (
    <DocumentReviewDialog
      key={documentPartsReviewKey(doc)}
      appId={appId}
      doc={doc}
      waiting={waiting.length}
      // Closing for any reason, a confirm included, moves on. A confirmed
      // list drops out on the refresh anyway; this keeps it from showing
      // again in the moment before that lands.
      onClose={() => setPutOff(prev => new Set(prev).add(key))}
      onChange={onChange}
    />
  )
}
